import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Fragment } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import {
  MdLocalShipping,
  MdCancel,
  MdPause,
  MdPlayArrow,
  MdEdit,
  MdVisibility,
} from "react-icons/md";

import { axiosGet, axiosPatch, axiosPost } from "api";
import CustomGrid from "components/lists/CustomGrid";
import FileDownloadButton from "components/Files/FileDownloadButton";
import OrderDetailDialog from "components/Orders/OrderDetailDialog";
import DispatchOrdersWizardModal from "components/Orders/DispatchOrdersWizardModal";
import EditOrderModal from "components/Orders/EditOrderModal";
import OrderPreparationReportsDialog from "components/Orders/OrderPreparationReportsDialog";
import OrderPreparationDialog from "components/Orders/OrderPreparationDialog";
import OrderStateReasonDialog from "components/Orders/OrderStateReasonDialog";
import {
  ORDER_STATUSES,
  STATUS_CODES,
} from "constants/index";
import {
  buildOrderDispatchBatchUrl,
  MOVEMENTS_URL,
  ORDERS_DISPATCH_BATCH_URL,
  getOrderCancelUrl,
  getOrderRemitoDownloadUrl,
  getOrderStatusUrl,
  ORDERS_STATUS_BATCH_URL,
  ORDERS_URL,
} from "constants/urls";
import {
  ORDER_STATUS_OPTIONS,
  buildOrderErrorMessage,
  canCancelOrder,
  canDispatchOrder,
  canEditOrder,
  canResumeOrder,
  canStopOrder,
  getOrderStatusChipColor,
  getOrderStatusLabel,
} from "helpers/orders";
import { queryBuilder } from "helpers/query";
import { formatDateOnlyDMY } from "helpers";
import { useConfirmationModal } from "hooks/useConfirmationModal";
import useCrud from "hooks/useCrud";
import { useToast } from "hooks/useToast";
import {
  Article,
  AdditionalItem,
  AssociatedClient,
  Client,
  Movement,
  OrderCancelPayload,
  Order,
  OrderBatchStatusUpdatePayload,
  OrderBatchDispatchPayload,
  OrderDispatchBatch,
  OrderDispatchBatchRelation,
  OrderListFilters,
  OrderStatusUpdatePayload,
  OrderStatus,
  OrderReservationStrategy,
  Store,
  User,
} from "types";

type Props = {
  clients: Client[];
  associatedClients: AssociatedClient[];
  additionalItems: AdditionalItem[];
  articles: Article[];
  stores: Store[];
  users: User[];
};

const defaultFilters = {
  clientId: undefined as number | undefined,
  associatedClientId: undefined as number | undefined,
  articleId: undefined as number | undefined,
  statuses: [] as OrderStatus[],
  vehicle: "",
  clientDocument: "",
  id: "",
  fromDate: null as Date | null,
  toDate: null as Date | null,
};

const ORDER_STATUS_FILTER_OPTIONS = ORDER_STATUS_OPTIONS.filter(
  (option) => option.id !== ORDER_STATUSES.ENTREGADO,
);

const ORDER_STATUSES_WITH_RELATED_DISCHARGE: OrderStatus[] = [
  ORDER_STATUSES.PREPARADO,
  ORDER_STATUSES.DESPACHADO,
];

const DISPATCH_BATCH_POLLING_INTERVAL_MS = 2000;

export default function OrdersWorkflowList({
  clients,
  associatedClients,
  additionalItems,
  articles,
  stores,
  users,
}: Props) {
  const { data: orders, fetchData, loading, count } = useCrud<Order>(ORDERS_URL, {
    disableAutoFetch: true,
  });
  const { showSnackBar } = useToast();
  const { showModal } = useConfirmationModal();
  const dispatchBatchPollingTimeoutRef = useRef<number | null>(null);

  const [filters, setFilters] = useState(defaultFilters);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailOrder, setDetailOrder] = useState<Order | null>(null);
  const [dischargeDetailOpen, setDischargeDetailOpen] = useState(false);
  const [dischargeDetailLoading, setDischargeDetailLoading] = useState(false);
  const [dischargeDetailMovement, setDischargeDetailMovement] =
    useState<Movement | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editOrder, setEditOrder] = useState<Order | null>(null);
  const [editLoadingId, setEditLoadingId] = useState<number | null>(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [preparationReportsOpen, setPreparationReportsOpen] = useState(false);
  const [preparationReportOrders, setPreparationReportOrders] = useState<Order[]>([]);
  const [ordersToPrepare, setOrdersToPrepare] = useState<Order[]>([]);
  const [preparationDialogOpen, setPreparationDialogOpen] = useState(false);
  const [rowActionLoadingKey, setRowActionLoadingKey] = useState<string | null>(
    null,
  );
  const [reasonDialogMode, setReasonDialogMode] = useState<"stop" | "cancel">(
    "stop",
  );
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [reasonDialogOrder, setReasonDialogOrder] = useState<Order | null>(null);
  const [dispatchResultModalOpen, setDispatchResultModalOpen] = useState(false);
  const [dispatchResultRelations, setDispatchResultRelations] = useState<
    OrderDispatchBatchRelation[]
  >([]);
  const [dispatchWizardOpen, setDispatchWizardOpen] = useState(false);
  const [dispatchWizardOrders, setDispatchWizardOrders] = useState<Order[]>([]);
  const [dispatchWizardLoading, setDispatchWizardLoading] = useState(false);

  const activeClients = useMemo(
    () => clients.filter((client) => client.is_active && client.parent_client_id == null),
    [clients],
  );

  const selectedClient = useMemo(
    () => activeClients.find((client) => client.id === filters.clientId) ?? null,
    [activeClients, filters.clientId],
  );

  const selectedAssociatedClient = useMemo(
    () =>
      associatedClients.find(
        (associatedClient) => associatedClient.id === filters.associatedClientId,
      ) ?? null,
    [associatedClients, filters.associatedClientId],
  );

  const associatedClientOptions = useMemo(
    () =>
      associatedClients.filter(
        (associatedClient) =>
          associatedClient.is_active &&
          (!filters.clientId || associatedClient.client_id === filters.clientId),
      ),
    [associatedClients, filters.clientId],
  );

  const articleOptions = useMemo(
    () =>
      articles.filter(
        (article) => !article.deleted_at && (!filters.clientId || article.client_id === filters.clientId),
      ),
    [articles, filters.clientId],
  );

  const selectedOrders = useMemo(() => {
    const selectedIds = new Set(selectionModel.map((value) => Number(value)));
    return orders.filter((order) => selectedIds.has(Number(order.id)));
  }, [orders, selectionModel]);

  const orderIdOptions = useMemo(
    () =>
      Array.from(new Set(orders.map((order) => Number(order.id))))
        .sort((left, right) => right - left)
        .map((id) => ({
          id,
          label: String(id),
        })),
    [orders],
  );

  const clientDocumentOptions = useMemo(
    () =>
      Array.from(
        new Set(
          orders
            .map((order) => order.client_document?.trim())
            .filter(
              (clientDocument): clientDocument is string =>
                Boolean(clientDocument),
            ),
        ),
      )
        .sort((left, right) => right.localeCompare(left))
        .map((clientDocument) => ({
          id: clientDocument,
          label: clientDocument,
        })),
    [orders],
  );

  const vehicleOptions = useMemo(
    () =>
      Array.from(
        new Set(
          orders
            .map((order) => order.vehicle?.trim())
            .filter((vehicle): vehicle is string => Boolean(vehicle)),
        ),
      )
        .sort((left, right) => left.localeCompare(right))
        .map((vehicle) => ({
          id: vehicle,
          label: vehicle,
        })),
    [orders],
  );

  const hasMultipleSelectedClients = useMemo(
    () => new Set(selectedOrders.map((order) => Number(order.client_id))).size > 1,
    [selectedOrders],
  );

  const canPrepare = useMemo(
    () =>
      selectedOrders.length > 0 &&
      selectedOrders.every(
        (order) => order.movement_order?.order_status === ORDER_STATUSES.INGRESADO,
      ),
    [selectedOrders],
  );

  const canMarkPrepared = useMemo(
    () =>
      selectedOrders.length > 0 &&
      selectedOrders.every(
        (order) =>
          order.movement_order?.order_status === ORDER_STATUSES.EN_PREPARACION,
      ),
    [selectedOrders],
  );

  const canPrintPreparationReports = useMemo(
    () =>
      selectedOrders.length > 0 &&
      selectedOrders.every(
        (order) =>
          ([ORDER_STATUSES.EN_PREPARACION, ORDER_STATUSES.PREPARADO] as OrderStatus[]).includes(
            order.movement_order?.order_status as OrderStatus,
          ),
      ),
    [selectedOrders],
  );

  const canDispatchSelected = useMemo(
    () =>
      selectedOrders.length > 0 &&
      selectedOrders.every(
        (order) => order.movement_order?.order_status === ORDER_STATUSES.PREPARADO,
      ),
    [selectedOrders],
  );

  const fetchOrders = useCallback(async () => {
    const query = queryBuilder<OrderListFilters>({
      clientId: filters.clientId,
      associatedClientId: filters.associatedClientId,
      articleId: filters.articleId,
      status: filters.statuses,
      vehicle: filters.vehicle.trim() || undefined,
      clientDocument: filters.clientDocument.trim() || undefined,
      id: filters.id ? Number(filters.id) : undefined,
      fromDate: filters.fromDate?.toISOString(),
      toDate: filters.toDate?.toISOString(),
    });

    await fetchData(query, true, {}, true);
    setSelectionModel([]);
  }, [fetchData, filters]);

  const fetchOrderById = useCallback(
    async (orderId: number): Promise<Order | null> => {
      const response = await axiosGet(`${ORDERS_URL}${orderId}`, true);

      if (response.status === STATUS_CODES.OK) {
        return response.data as Order;
      }

      showSnackBar(buildOrderErrorMessage(response.data), "error");
      return null;
    },
    [showSnackBar],
  );

  const fetchMovementById = useCallback(
    async (movementId: number): Promise<Movement | null> => {
      const response = await axiosGet(`${MOVEMENTS_URL}${movementId}`, true);

      if (response.status === STATUS_CODES.OK) {
        const movement = Array.isArray(response.data)
          ? response.data[0]
          : response.data;

        return (movement as Movement | undefined) ?? null;
      }

      showSnackBar(buildOrderErrorMessage(response.data), "error");
      return null;
    },
    [showSnackBar],
  );

  const clearDispatchBatchPolling = useCallback(() => {
    if (dispatchBatchPollingTimeoutRef.current !== null) {
      window.clearTimeout(dispatchBatchPollingTimeoutRef.current);
      dispatchBatchPollingTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    fetchData("", true, {}, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      clearDispatchBatchPolling();
    };
  }, [clearDispatchBatchPolling]);

  const handleClearFilters = () => {
    setFilters({ ...defaultFilters });
    setSelectionModel([]);
    fetchData("", true, {}, true);
  };

  const handleOpenDetail = useCallback(
    async (orderId: number) => {
      setDetailOpen(true);
      setDetailLoading(true);

      try {
        const order = await fetchOrderById(orderId);
        setDetailOrder(order);
      } finally {
        setDetailLoading(false);
      }
    },
    [fetchOrderById],
  );

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setDetailOrder(null);
    setDetailLoading(false);
  };

  const handleOpenDischargeDetail = useCallback(
    async (dischargeMovementId: number | null | undefined) => {
      setDischargeDetailOpen(true);
      setDischargeDetailLoading(true);

      try {
        if (!dischargeMovementId) {
          showSnackBar("El pedido no tiene un egreso relacionado.", "warning");
          setDischargeDetailOpen(false);
          return;
        }

        const movement = await fetchMovementById(dischargeMovementId);
        setDischargeDetailMovement(movement);
      } finally {
        setDischargeDetailLoading(false);
      }
    },
    [fetchMovementById, showSnackBar],
  );

  const handleCloseDischargeDetail = () => {
    setDischargeDetailOpen(false);
    setDischargeDetailMovement(null);
    setDischargeDetailLoading(false);
  };

  const handleOpenEdit = useCallback(
    async (orderId: number) => {
      setEditLoadingId(orderId);

      try {
        const order = await fetchOrderById(orderId);

        if (!order) {
          return;
        }

        setEditOrder(order);
        setEditOpen(true);
      } finally {
        setEditLoadingId(null);
      }
    },
    [fetchOrderById],
  );

  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditOrder(null);
    setEditLoadingId(null);
  };

  const handleClosePreparationReports = () => {
    setPreparationReportsOpen(false);
    setPreparationReportOrders([]);
  };

  const handleCloseReasonDialog = () => {
    if (rowActionLoadingKey) {
      return;
    }

    setReasonDialogOpen(false);
    setReasonDialogOrder(null);
    setReasonDialogMode("stop");
  };

  const refreshOrdersAfterAction = useCallback(async () => {
    await fetchOrders();

    if (detailOpen && detailOrder?.id) {
      await handleOpenDetail(Number(detailOrder.id));
    }
  }, [detailOpen, detailOrder?.id, fetchOrders, handleOpenDetail]);

  const handleBatchStatusUpdate = async (
    status: OrderStatus,
    ordersToUpdate = selectedOrders,
    reservationStrategy?: OrderReservationStrategy,
  ) => {
    const payload: OrderBatchStatusUpdatePayload = {
      ids: ordersToUpdate.map((order) => Number(order.id)),
      status,
      reason: null,
      ...(reservationStrategy ? { reservation_strategy: reservationStrategy } : {}),
    };

    const response = await axiosPatch(ORDERS_STATUS_BATCH_URL, payload, true);
    const updatedOrders = Array.isArray(response.data)
      ? (response.data as Order[])
      : [];

    if (response.status === STATUS_CODES.OK) {
      showSnackBar(
        status === ORDER_STATUSES.EN_PREPARACION
          ? "Pedidos enviados a preparación correctamente."
          : "Pedidos marcados como preparados correctamente.",
        "success",
      );
      if (status === ORDER_STATUSES.EN_PREPARACION) {
        setPreparationDialogOpen(false);
        setOrdersToPrepare([]);
        if (updatedOrders.length > 0) {
          setPreparationReportOrders(updatedOrders);
          setPreparationReportsOpen(true);
        }
      }

      await fetchOrders();
      return;
    }

    showSnackBar(buildOrderErrorMessage(response.data), "error");
  };

  const handleClosePreparationDialog = () => {
    if (actionLoading === ORDER_STATUSES.EN_PREPARACION) return;
    setPreparationDialogOpen(false);
    setOrdersToPrepare([]);
  };

  const handleConfirmPreparation = async (strategy: OrderReservationStrategy) => {
    if (actionLoading !== null || ordersToPrepare.length === 0) return;
    setActionLoading(ORDER_STATUSES.EN_PREPARACION);
    try {
      await handleBatchStatusUpdate(ORDER_STATUSES.EN_PREPARACION, ordersToPrepare, strategy);
    } finally {
      setActionLoading(null);
    }
  };

  const handlePrintPreparationReports = async () => {
    setReportsLoading(true);

    try {
      const responses = await Promise.all(
        selectedOrders.map((order) =>
          axiosGet(`${ORDERS_URL}${Number(order.id)}`, true),
        ),
      );

      const invalidResponse = responses.find(
        (response) => response.status !== STATUS_CODES.OK,
      );

      if (invalidResponse) {
        showSnackBar(buildOrderErrorMessage(invalidResponse.data), "error");
        return;
      }

      setPreparationReportOrders(
        responses.map((response) => response.data as Order),
      );
      setPreparationReportsOpen(true);
    } finally {
      setReportsLoading(false);
    }
  };

  const dispatchOrders = useCallback(async (payload: OrderBatchDispatchPayload) => {
    const response = await axiosPost(ORDERS_DISPATCH_BATCH_URL, payload, true);

    return {
      status: response.status,
      batchId: Number(response.data?.batchId),
      error: response.data,
    };
  }, []);

  const handleFailedDispatchBatch = useCallback(
    (batch: OrderDispatchBatch | null, fallbackMessage?: string) => {
      clearDispatchBatchPolling();
      showSnackBar(
        batch?.result?.message ??
          fallbackMessage ??
          "No se pudo procesar el lote de despacho.",
        "error",
      );
    },
    [clearDispatchBatchPolling, showSnackBar],
  );

  const handleSuccessfulDispatchBatch = useCallback(
    async (batch: OrderDispatchBatch) => {
      clearDispatchBatchPolling();
      const dischargeIds = (batch.result?.discharge_ids ?? []).filter((id) =>
        Number.isFinite(id),
      );
      const relations = (batch.result?.relations ?? []).filter(
        (relation) =>
          Number.isFinite(Number(relation.order_id)) &&
          Number.isFinite(Number(relation.discharge_id)),
      );
      const processedOrdersCount = batch.order_ids.length;

      if (relations.length > 0) {
        setDispatchResultRelations(relations);
        setDispatchResultModalOpen(true);
      }

      showSnackBar(
        processedOrdersCount === 1
          ? dischargeIds[0]
            ? `Pedido despachado correctamente. Egreso #${dischargeIds[0]} generado.`
            : "Pedido despachado correctamente."
          : dischargeIds.length > 0
            ? `Pedidos despachados correctamente. Egresos generados: ${dischargeIds
                .map(String)
                .join(", ")}.`
            : "Pedidos despachados correctamente.",
        "success",
      );
      await fetchOrders();
    },
    [clearDispatchBatchPolling, fetchOrders, showSnackBar],
  );

  const handleCloseDispatchResultModal = useCallback(() => {
    setDispatchResultModalOpen(false);
    setDispatchResultRelations([]);
  }, []);

  const handleCloseDispatchWizard = useCallback(() => {
    if (actionLoading === "dispatch") {
      return;
    }

    setDispatchWizardOpen(false);
    setDispatchWizardOrders([]);
    setDispatchWizardLoading(false);
  }, [actionLoading]);

  const openDispatchWizard = useCallback(
    async (ordersToDispatch: Order[]) => {
      setDispatchWizardLoading(true);

      try {
        const detailedOrders = await Promise.all(
          ordersToDispatch.map((order) => fetchOrderById(Number(order.id))),
        );
        const validOrders = detailedOrders.filter(Boolean) as Order[];

        if (validOrders.length !== ordersToDispatch.length) {
          return;
        }

        setDispatchWizardOrders(validOrders);
        setDispatchWizardOpen(true);
      } finally {
        setDispatchWizardLoading(false);
      }
    },
    [fetchOrderById],
  );

  const pollOrderDispatchBatch = useCallback(
    async (batchId: number): Promise<void> => {
      const batchResponse = await axiosGet(buildOrderDispatchBatchUrl(batchId), true);

      if (batchResponse.status !== STATUS_CODES.OK) {
        handleFailedDispatchBatch(
          null,
          batchResponse.data?.message ?? "No se pudo consultar el lote de despacho.",
        );
        return;
      }

      const batch = batchResponse.data as OrderDispatchBatch;

      if (batch.status === "pending" || batch.status === "processing") {
        await new Promise<void>((resolve) => {
          dispatchBatchPollingTimeoutRef.current = window.setTimeout(
            resolve,
            DISPATCH_BATCH_POLLING_INTERVAL_MS,
          );
        });

        return pollOrderDispatchBatch(batchId);
      }

      if (batch.status === "success") {
        await handleSuccessfulDispatchBatch(batch);
        return;
      }

      handleFailedDispatchBatch(batch);
    },
    [handleFailedDispatchBatch, handleSuccessfulDispatchBatch],
  );

  const runDispatchOrders = useCallback(
    async (payload: OrderBatchDispatchPayload) => {
      clearDispatchBatchPolling();

      const { status, batchId, error } = await dispatchOrders(payload);

      if (status !== STATUS_CODES.ACCEPTED || !Number.isFinite(batchId)) {
        showSnackBar(buildOrderErrorMessage(error), "error");
        return false;
      }

      await pollOrderDispatchBatch(batchId);
      return true;
    },
    [
      clearDispatchBatchPolling,
      dispatchOrders,
      pollOrderDispatchBatch,
      showSnackBar,
    ],
  );

  const handleDispatchOrdersBatch = useCallback(
    async (payload?: OrderBatchDispatchPayload) => {
      return await runDispatchOrders(
        payload ?? {
          ids: selectedOrders.map((order) => Number(order.id)),
        },
      );
    },
    [runDispatchOrders, selectedOrders],
  );

  const handleDispatchWizardConfirm = useCallback(
    async (payload: OrderBatchDispatchPayload) => {
      setActionLoading("dispatch");

      try {
        return await handleDispatchOrdersBatch(payload);
      } finally {
        setActionLoading(null);
      }
    },
    [handleDispatchOrdersBatch],
  );

  const confirmBatchStatusUpdate = (status: OrderStatus) => {
    if (status === ORDER_STATUSES.EN_PREPARACION) {
      setOrdersToPrepare([...selectedOrders]);
      setPreparationDialogOpen(true);
      return;
    }
    showModal({
      title: "Confirmar",
      message: `Se van a marcar como preparados ${selectedOrders.length} pedido(s).`,
      action: async () => {
        setActionLoading(status);
        try {
          await handleBatchStatusUpdate(status);
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  const confirmBatchDispatch = useCallback(() => {
    void openDispatchWizard(selectedOrders);
    const shouldUseLegacyDispatchModal = false;
    if (shouldUseLegacyDispatchModal) { showModal({
      title: "Despachar pedidos",
      message: `Se generarán egresos para ${selectedOrders.length} pedido(s) y todos pasarán a despachado.`,
      action: async () => {
        setActionLoading("dispatch");
        try {
          await handleDispatchOrdersBatch();
        } finally {
          setActionLoading(null);
        }
      },
    }); }
  }, [
    handleDispatchOrdersBatch,
    openDispatchWizard,
    selectedOrders,
    showModal,
  ]);

  const handleOrderStatusUpdate = useCallback(
    async (order: Order, payload: OrderStatusUpdatePayload) => {
      const loadingKey = `${payload.status}:${order.id}`;
      setRowActionLoadingKey(loadingKey);

      try {
        const response = await axiosPatch(
          getOrderStatusUrl(Number(order.id)),
          payload,
          true,
        );

        if (response.status === STATUS_CODES.OK) {
          showSnackBar(
            payload.status === ORDER_STATUSES.DETENIDO
              ? "Pedido detenido correctamente."
              : "Pedido reactivado correctamente.",
            "success",
          );
          setReasonDialogOpen(false);
          setReasonDialogOrder(null);
          await refreshOrdersAfterAction();
          return;
        }

        showSnackBar(buildOrderErrorMessage(response.data), "error");
      } finally {
        setRowActionLoadingKey(null);
      }
    },
    [refreshOrdersAfterAction, showSnackBar],
  );

  const handleCancelOrder = async (order: Order, reason: string) => {
    const loadingKey = `cancel:${order.id}`;
    setRowActionLoadingKey(loadingKey);

    try {
      const payload: OrderCancelPayload = {
        reason,
      };

      const response = await axiosPost(
        getOrderCancelUrl(Number(order.id)),
        payload,
        true,
      );

      if (response.status === STATUS_CODES.OK) {
        showSnackBar("Pedido cancelado correctamente.", "success");
        setReasonDialogOpen(false);
        setReasonDialogOrder(null);
        await refreshOrdersAfterAction();
        return;
      }

      showSnackBar(buildOrderErrorMessage(response.data), "error");
    } finally {
      setRowActionLoadingKey(null);
    }
  };

  const handleResumeOrder = useCallback(
    (order: Order) => {
      const previousStatus = order.movement_order?.previous_order_status;

      if (!previousStatus) {
        showSnackBar(
          "El pedido detenido no tiene un estado previo para reactivarse.",
          "error",
        );
        return;
      }

      showModal({
        title: "Reactivar pedido",
        message: `El pedido ${order.id} volverá al estado ${getOrderStatusLabel(previousStatus)}.`,
        action: async () => {
          await handleOrderStatusUpdate(order, {
            status: previousStatus,
            reason: null,
          });
        },
      });
    },
    [handleOrderStatusUpdate, showModal, showSnackBar],
  );

  const handleDispatchOrder = useCallback(
    async (order: Order) => {
      const loadingKey = `dispatch:${order.id}`;
      setRowActionLoadingKey(loadingKey);

      try {
        await openDispatchWizard([order]);
      } finally {
        setRowActionLoadingKey(null);
      }
    },
    [openDispatchWizard],
  );

  const confirmDispatchOrder = useCallback(
    (order: Order) => {
      void handleDispatchOrder(order);
      const shouldUseLegacyDispatchModal = false;
      if (shouldUseLegacyDispatchModal) { showModal({
        title: "Despachar pedido",
        message: `Se generará un egreso en base al pedido ${order.id} y el pedido pasará a despachado.`,
        action: async () => {
          await handleDispatchOrder(order);
        },
      }); }
    },
    [handleDispatchOrder, showModal],
  );

  const openStopDialog = (order: Order) => {
    setReasonDialogMode("stop");
    setReasonDialogOrder(order);
    setReasonDialogOpen(true);
  };

  const openCancelDialog = (order: Order) => {
    setReasonDialogMode("cancel");
    setReasonDialogOrder(order);
    setReasonDialogOpen(true);
  };

  const handleSubmitReasonDialog = async (reason: string) => {
    if (!reasonDialogOrder) {
      return;
    }

    if (reasonDialogMode === "cancel") {
      await handleCancelOrder(reasonDialogOrder, reason);
      return;
    }

    await handleOrderStatusUpdate(reasonDialogOrder, {
      status: ORDER_STATUSES.DETENIDO,
      reason,
    });
  };

  const columns = useMemo<GridColDef<Order>[]>(
    () => [
      {
        field: "id",
        headerName: "Nro Pedido",
        headerAlign: "center",
        align: "center",
        minWidth: 110,
        flex: 0.7,
      },
      {
        field: "client",
        headerName: "Cliente",
        headerAlign: "center",
        minWidth: 220,
        flex: 1.4,
        valueGetter: (params) =>
          params.row.client
            ? `${params.row.client.code} - ${params.row.client.razon_social}`
            : "-",
      },
      {
        field: "associated_client",
        headerName: "Cliente Asociado",
        headerAlign: "center",
        minWidth: 180,
        flex: 1,
        valueGetter: (params) =>
          params.row.movement_order?.associated_client?.razon_social ?? "-",
      },
      {
        field: "client_document",
        headerName: "Doc. Cliente",
        headerAlign: "center",
        align: "center",
        minWidth: 140,
        flex: 0.9,
        valueGetter: (params) => params.row.client_document?.trim() || "-",
      },
      {
        field: "vehicle",
        headerName: "Vehiculo",
        headerAlign: "center",
        align: "center",
        minWidth: 140,
        flex: 0.9,
        valueGetter: (params) => params.row.vehicle?.trim() || "-",
      },
      {
        field: "received_at",
        headerName: "Fecha Pedido",
        headerAlign: "center",
        align: "center",
        minWidth: 130,
        flex: 0.8,
        valueGetter: (params) =>
          formatDateOnlyDMY(params.row.movement_order?.received_at) || "-",
      },
      {
        field: "preparation_date",
        headerName: "Fecha Preparación",
        headerAlign: "center",
        align: "center",
        minWidth: 150,
        flex: 0.9,
        valueGetter: (params) =>
          formatDateOnlyDMY(params.row.movement_order?.preparation_date) || "-",
      },
      {
        field: "status",
        headerName: "Estado",
        headerAlign: "center",
        align: "center",
        minWidth: 160,
        flex: 0.9,
        sortable: false,
        renderCell: (params) => (
          <Chip
            label={getOrderStatusLabel(params.row.movement_order?.order_status)}
            color={getOrderStatusChipColor(params.row.movement_order?.order_status)}
            size="small"
          />
        ),
      },
      {
        field: "pallets",
        headerName: "Pallets",
        headerAlign: "center",
        align: "center",
        minWidth: 90,
        flex: 0.6,
      },
      {
        field: "weight",
        headerName: "Peso",
        headerAlign: "center",
        align: "center",
        minWidth: 100,
        flex: 0.7,
      },
      {
        field: "total_units",
        headerName: "Und Totales",
        headerAlign: "center",
        align: "center",
        minWidth: 110,
        flex: 0.8,
        valueGetter: (params) => params.row.movement_order?.total_units ?? 0,
      },
      {
        field: "observation",
        headerName: "Observación",
        headerAlign: "center",
        align: "left",
        minWidth: 180,
        flex: 1.2,
        sortable: false,
        valueGetter: (params) => params.row.observation?.trim() || "-",
      },
      {
        field: "discharge",
        headerName: "Egreso",
        headerAlign: "center",
        align: "center",
        minWidth: 90,
        flex: 0.6,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const hasRelatedDischargeStatus =
            ORDER_STATUSES_WITH_RELATED_DISCHARGE.includes(
              params.row.movement_order?.order_status,
            );
          const dischargeMovementId = params.row.discharge_movement_id;

          if (!hasRelatedDischargeStatus || !dischargeMovementId) return "-";

          return (
            <Tooltip title="Ver egreso relacionado">
              <IconButton
                size="small"
                onClick={() => {
                  void handleOpenDischargeDetail(dischargeMovementId);
                }}
              >
                <MdVisibility />
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        field: "detail",
        headerName: "Detalle",
        headerAlign: "center",
        align: "center",
        minWidth: 90,
        flex: 0.6,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <IconButton
            size="small"
            onClick={() => handleOpenDetail(Number(params.row.id))}
          >
            <MdVisibility />
          </IconButton>
        ),
      },
      {
        field: "actions",
        headerName: "Acciones",
        headerAlign: "center",
        align: "center",
        minWidth: 190,
        flex: 1.1,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const order = params.row;
          const status = order.movement_order?.order_status;
          const loadingForThisRow =
            rowActionLoadingKey !== null &&
            rowActionLoadingKey.endsWith(`:${order.id}`);
          const isEditingThisRow = editLoadingId === Number(order.id);

          return (
            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
              {Boolean(order.movement_order?.pdf_file_id) && (
                <FileDownloadButton
                  requestUrl={getOrderRemitoDownloadUrl(Number(order.id))}
                  label="Descargar remito"
                  iconOnly
                />
              )}
              <Tooltip title="Editar pedido">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => {
                      void handleOpenEdit(Number(order.id));
                    }}
                    disabled={
                      loadingForThisRow || isEditingThisRow || !canEditOrder(order)
                    }
                  >
                    <MdEdit />
                  </IconButton>
                </span>
              </Tooltip>

              {canStopOrder(order) && (
                <Tooltip title="Detener pedido">
                  <span>
                    <IconButton
                      size="small"
                      color="warning"
                      onClick={() => openStopDialog(order)}
                      disabled={loadingForThisRow}
                    >
                      <MdPause />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {canResumeOrder(order) && (
                <Tooltip
                  title={`Reactivar a ${getOrderStatusLabel(
                    order.movement_order?.previous_order_status ?? undefined,
                  )}`}
                >
                  <span>
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => handleResumeOrder(order)}
                      disabled={loadingForThisRow}
                    >
                      <MdPlayArrow />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {canCancelOrder(order) && (
                <Tooltip title="Cancelar pedido">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => openCancelDialog(order)}
                      disabled={loadingForThisRow || status === ORDER_STATUSES.CANCELADO}
                    >
                      <MdCancel />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {canDispatchOrder(order) && (
                <Tooltip title="Despachar pedido">
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => confirmDispatchOrder(order)}
                      disabled={loadingForThisRow || dispatchWizardLoading}
                    >
                      <MdLocalShipping />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ],
    [
      confirmDispatchOrder,
      dispatchWizardLoading,
      editLoadingId,
      handleOpenDischargeDetail,
      handleOpenDetail,
      handleOpenEdit,
      handleResumeOrder,
      rowActionLoadingKey,
    ],
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">Listado y preparación de pedidos</Typography>
            </Grid>

            <Grid item md={3} sm={6} xs={12}>
              <Autocomplete
                options={activeClients}
                value={selectedClient}
                getOptionLabel={(option) => `${option.code} - ${option.razon_social}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) =>
                  setFilters((prev) => ({
                    ...prev,
                    clientId: value?.id,
                    associatedClientId: undefined,
                    articleId: undefined,
                  }))
                }
                renderInput={(params) => <TextField {...params} label="Cliente" />}
                disabled={loading}
              />
            </Grid>

            <Grid item md={3} sm={6} xs={12}>
              <Autocomplete
                options={associatedClientOptions}
                value={selectedAssociatedClient}
                getOptionLabel={(option) => option.razon_social}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) =>
                  setFilters((prev) => ({
                    ...prev,
                    associatedClientId: value?.id,
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Cliente Asociado" />
                )}
                disabled={loading}
              />
            </Grid>

            <Grid item md={3} sm={6} xs={12}>
              <Autocomplete
                options={articleOptions}
                value={
                  articleOptions.find((article) => article.id === filters.articleId) ??
                  null
                }
                getOptionLabel={(option) => `${option.code} - ${option.description}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) =>
                  setFilters((prev) => ({
                    ...prev,
                    articleId: value?.id,
                  }))
                }
                renderInput={(params) => <TextField {...params} label="Artículo" />}
                disabled={loading}
              />
            </Grid>

            <Grid item md={3} sm={6} xs={12}>
              <Autocomplete
                multiple
                options={ORDER_STATUS_FILTER_OPTIONS}
                value={ORDER_STATUS_FILTER_OPTIONS.filter((option) =>
                  filters.statuses.includes(option.id),
                )}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, values) =>
                  setFilters((prev) => ({
                    ...prev,
                    statuses: values.map((value) => value.id),
                  }))
                }
                renderInput={(params) => <TextField {...params} label="Estado" />}
                disabled={loading}
              />
            </Grid>

            <Grid item md={3} sm={6} xs={12}>
              <Autocomplete
                options={orderIdOptions}
                value={
                  orderIdOptions.find((option) => option.id === Number(filters.id)) ??
                  null
                }
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) =>
                  setFilters((prev) => ({
                    ...prev,
                    id: value?.id ? String(value.id) : "",
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Nro Pedido" />
                )}
                disabled={loading}
              />
            </Grid>

            <Grid item md={3} sm={6} xs={12}>
              <Autocomplete
                options={vehicleOptions}
                value={
                  vehicleOptions.find((option) => option.id === filters.vehicle) ?? null
                }
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) =>
                  setFilters((prev) => ({
                    ...prev,
                    vehicle: value?.id ?? "",
                  }))
                }
                renderInput={(params) => <TextField {...params} label="Vehiculo" />}
                disabled={loading}
              />
            </Grid>

            <Grid item md={3} sm={6} xs={12}>
              <Autocomplete
                options={clientDocumentOptions}
                value={
                  clientDocumentOptions.find(
                    (option) => option.id === filters.clientDocument,
                  ) ?? null
                }
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) =>
                  setFilters((prev) => ({
                    ...prev,
                    clientDocument: value?.id ?? "",
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Doc. Cliente" />
                )}
                disabled={loading}
              />
            </Grid>

            <Grid item md={3} sm={6} xs={12}>
              <DatePicker
                label="Fecha Desde"
                value={filters.fromDate}
                format="dd/MM/yyyy"
                onChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    fromDate: value,
                  }))
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Grid>

            <Grid item md={3} sm={6} xs={12}>
              <DatePicker
                label="Fecha Hasta"
                value={filters.toDate}
                format="dd/MM/yyyy"
                onChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    toDate: value,
                  }))
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button variant="contained" onClick={fetchOrders} disabled={loading}>
                  Buscar
                </Button>
                <Button variant="outlined" onClick={handleClearFilters} disabled={loading}>
                  Limpiar filtros
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center" mb={1}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6">Pedidos cargados</Typography>
              <Typography variant="body2" color="text.secondary">
                Total: {count || orders.length}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  color="warning"
                  disabled={!canPrepare || actionLoading !== null || reportsLoading}
                  onClick={() => confirmBatchStatusUpdate(ORDER_STATUSES.EN_PREPARACION)}
                >
                  Enviar a preparación
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={
                    !canPrintPreparationReports ||
                    actionLoading !== null ||
                    reportsLoading
                  }
                  onClick={handlePrintPreparationReports}
                >
                  Imprimir reportes
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  disabled={!canMarkPrepared || actionLoading !== null || reportsLoading}
                  onClick={() => confirmBatchStatusUpdate(ORDER_STATUSES.PREPARADO)}
                >
                  Marcar preparado
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={
                    !canDispatchSelected ||
                    actionLoading !== null ||
                    reportsLoading ||
                    dispatchWizardLoading
                  }
                  onClick={confirmBatchDispatch}
                >
                  Despachar
                </Button>
              </Box>
            </Grid>
          </Grid>

          {selectedOrders.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Seleccionados: {selectedOrders.length}.{" "}
              {hasMultipleSelectedClients
                ? "La selección incluye múltiples clientes. Podrás cambiar estado, imprimir reportes y despachar dentro del mismo lote."
                : "Podés ejecutar acciones masivas sobre los pedidos seleccionados."}
            </Alert>
          )}

          {loading ? (
            <Grid item xs={12} display="flex" justifyContent="center" my={6}>
              <CircularProgress />
            </Grid>
          ) : (
            <CustomGrid<Order>
              structure={{
                name: "orders_workflow",
                labelName: "Pedidos",
                fields: columns,
              }}
              rows={orders}
              selectionModel={selectionModel}
              onRowSelection={setSelectionModel}
              checkboxSelection
            />
          )}

          {!loading && orders.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No hay pedidos para los filtros seleccionados.
            </Alert>
          )}
        </Paper>
      </Grid>

      <OrderDetailDialog
        open={detailOpen}
        loading={detailLoading}
        order={detailOrder}
        onClose={handleCloseDetail}
      />
      <OrderDetailDialog
        open={dischargeDetailOpen}
        loading={dischargeDetailLoading}
        movement={dischargeDetailMovement}
        onClose={handleCloseDischargeDetail}
        title="Detalle del egreso"
      />
      <OrderPreparationReportsDialog
        open={preparationReportsOpen}
        orders={preparationReportOrders}
        onClose={handleClosePreparationReports}
      />
      <OrderPreparationDialog
        open={preparationDialogOpen}
        orders={ordersToPrepare}
        loading={actionLoading === ORDER_STATUSES.EN_PREPARACION}
        onClose={handleClosePreparationDialog}
        onConfirm={handleConfirmPreparation}
      />
      <OrderStateReasonDialog
        open={reasonDialogOpen}
        mode={reasonDialogMode}
        order={reasonDialogOrder}
        loading={rowActionLoadingKey !== null}
        onClose={handleCloseReasonDialog}
        onSubmit={handleSubmitReasonDialog}
      />
      <Dialog
        open={dispatchResultModalOpen}
        onClose={handleCloseDispatchResultModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Pedidos despachados correctamente</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Pedido</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Egreso</Typography>
            </Grid>

            {dispatchResultRelations.map((relation) => (
              <Fragment
                key={`dispatch-relation-${relation.order_id}-${relation.discharge_id}`}
              >
                <Grid item xs={6}>
                  <Typography>Pedido #{relation.order_id}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>Egreso #{relation.discharge_id}</Typography>
                </Grid>
              </Fragment>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDispatchResultModal}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      {editOrder && (
        <EditOrderModal
          open={editOpen}
          order={editOrder}
          articles={articles}
          associatedClients={associatedClients}
          stores={stores}
          users={users}
          onClose={handleCloseEdit}
          onUpdate={() => {
            void refreshOrdersAfterAction();
          }}
        />
      )}
      <DispatchOrdersWizardModal
        open={dispatchWizardOpen}
        loading={dispatchWizardLoading || actionLoading === "dispatch"}
        orders={dispatchWizardOrders}
        clients={clients}
        associatedClients={associatedClients}
        articles={articles}
        additionalItems={additionalItems}
        stores={stores}
        users={users}
        onClose={handleCloseDispatchWizard}
        onConfirm={handleDispatchWizardConfirm}
      />
    </Grid>
  );
}
